import { useState } from 'react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines, faUsers, faCircleCheck, faCrown,
  faShieldHalved, faUserShield, faTriangleExclamation,
  faHourglassHalf, faSpinner, faCircleXmark, faFilePdf
} from '@fortawesome/free-solid-svg-icons'
import SuperadminSidebar from '../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../components/superadmin/Topbar'

const STATUS_STYLE = {
  menunggu: 'bg-blue-900/50 text-blue-300 ring-1 ring-blue-700',
  diproses: 'bg-amber-900/40 text-amber-300 ring-1 ring-amber-700',
  selesai:  'bg-emerald-900/40 text-emerald-300 ring-1 ring-emerald-700',
  ditolak:  'bg-red-900/40 text-red-300 ring-1 ring-red-700',
}

const ACTION_ICON = {
  status_changed: faCircleCheck,
  identity_requested: faShieldHalved,
  identity_approved: faUserShield,
  submitted: faFileLines,
}

export default function SuperadminDashboard({
  current_user,
  stats = {},
  recent_reports = [],
  recent_activities = [],
  failed_logins = [],
}) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const statCards = [
    {
      label: 'Total Laporan',
      value: stats.total_reports?.toLocaleString('id-ID') ?? '0',
      delta: stats.report_delta?.value ?? '0%',
      up: stats.report_delta?.up ?? true,
      icon: faFileLines,
      color: 'indigo',
    },
    {
      label: 'Admin Aktif',
      value: stats.active_admins?.toLocaleString('id-ID') ?? '0',
      delta: `${stats.menunggu ?? 0} menunggu`,
      up: true,
      icon: faUserShield,
      color: 'sky',
    },
    {
      label: 'Pengguna',
      value: stats.total_users?.toLocaleString('id-ID') ?? '0',
      delta: stats.user_delta?.value ?? '0%',
      up: stats.user_delta?.up ?? true,
      icon: faUsers,
      color: 'violet',
    },
    {
      label: 'Resolusi Rate',
      value: `${stats.resolution_rate ?? 0}%`,
      delta: `${stats.diproses ?? 0} diproses`,
      up: (stats.resolution_rate ?? 0) >= 50,
      icon: faCircleCheck,
      color: 'emerald',
    },
  ]

  const COLOR_MAP = {
    indigo:  { icon: 'bg-indigo-900/60',  text: 'text-indigo-400'  },
    sky:     { icon: 'bg-sky-900/60',     text: 'text-sky-400'     },
    violet:  { icon: 'bg-violet-900/60',  text: 'text-violet-400'  },
    emerald: { icon: 'bg-emerald-900/60', text: 'text-emerald-400' },
  }

  return (
    <div className="flex h-screen bg-gray-950 font-sans overflow-hidden text-gray-100">
      <SuperadminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <SuperadminTopbar
          current_user={current_user}
          onMenuToggle={() => setSidebarOpen(true)}
          placeholder="Cari laporan, admin, pengguna..."
        />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faCrown} className="w-4 h-4 text-indigo-400" />
              <h1 className="text-xl font-bold text-white">Dashboard Superadmin</h1>
            </div>
            <p className="text-sm text-gray-500">
              Selamat datang, <span className="font-medium text-gray-300">{current_user?.name}</span> — akses penuh sistem WBS
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {statCards.map(stat => {
              const c = COLOR_MAP[stat.color]
              return (
                <div key={stat.label} className="bg-gray-900 rounded-xl border border-gray-800 p-5 flex items-start gap-4">
                  <div className={`w-11 h-11 ${c.icon} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <FontAwesomeIcon icon={stat.icon} className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                    <div className={`mt-1.5 text-xs font-medium ${stat.up ? 'text-emerald-400' : 'text-red-400'}`}>
                      {stat.delta} · bulan ini
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden mb-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <h2 className="text-sm font-semibold text-gray-100">Laporan Terbaru</h2>
              <a href="/superadmin/reports" className="text-xs font-medium text-indigo-400 hover:text-indigo-300">Lihat semua →</a>
            </div>
            <div className="overflow-x-auto">
              {recent_reports.length === 0 ? (
                <p className="px-5 py-8 text-sm text-gray-500 text-center">Belum ada laporan.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-800/50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="text-left px-5 py-3">Kode</th>
                      <th className="text-left px-5 py-3">Judul Laporan</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3">Tanggal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {recent_reports.map(row => (
                      <tr key={row.id} className="hover:bg-gray-800/40 transition-colors">
                        <td className="px-5 py-3.5 font-mono text-xs text-gray-500">{row.tracking_code}</td>
                        <td className="px-5 py-3.5 text-gray-300 font-medium max-w-xs truncate">
                          <a href={`/superadmin/reports/${row.id}`} className="hover:text-indigo-400 transition-colors">{row.title}</a>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[row.status] || STATUS_STYLE.menunggu}`}>
                            {row.status_label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 text-xs whitespace-nowrap">{row.created_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-gray-900 rounded-xl border border-gray-800 p-5">
              <h2 className="text-sm font-semibold text-gray-100 mb-4">Aktivitas Terbaru</h2>
              {recent_activities.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada aktivitas.</p>
              ) : (
                <ul className="space-y-3">
                  {recent_activities.map((a, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FontAwesomeIcon icon={ACTION_ICON[a.action_type] || faFileLines} className="w-3.5 h-3.5 text-indigo-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-400">{a.text}</p>
                        {a.detail && <p className="text-xs text-gray-600">{a.detail}</p>}
                        <p className="text-xs text-gray-600 mt-0.5">{a.time}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
              <h2 className="text-sm font-semibold text-gray-100 mb-4">Ringkasan Sistem</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <FontAwesomeIcon icon={faHourglassHalf} className="w-3.5 h-3.5" /> Menunggu
                  </span>
                  <span className="font-semibold text-amber-400">{stats.menunggu ?? 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5" /> Diproses
                  </span>
                  <span className="font-semibold text-sky-400">{stats.diproses ?? 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilePdf} className="w-3.5 h-3.5" /> Dokumen
                  </span>
                  <span className="font-semibold text-violet-400">{stats.documents ?? 0}</span>
                </div>
              </div>

              {failed_logins.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-800">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="w-3.5 h-3.5 text-amber-400" />
                    Login Gagal Terbaru
                  </h3>
                  <ul className="space-y-2">
                    {failed_logins.map((l, i) => (
                      <li key={i} className="text-xs">
                        <p className="text-gray-400">{l.text}</p>
                        <p className="text-gray-600">{l.ip} · {l.time}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 flex items-center gap-2 text-xs text-gray-500">
                <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                Data diperbarui saat halaman dimuat
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
